import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { IconBillCheck, IconEdit, IconEye, IconStore } from "../../components/layout/icons";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/Feedback";
import { ModuleHero } from "../../components/ui/ModuleHero";
import { SearchableSelect } from "../../components/ui/SearchableSelect";
import { ApiError } from "../../lib/api-client";
import { useToast } from "../../lib/toast";
import { createStoreRecord, patchStoreRecord, updateStoreMiscellaneousBillStatus } from "./store.api";
import { useStoreLookup, useStoreRows } from "./store.queries";
import "./store.css";

type BillTab = "create" | "verify";
type BillForm = {
  billDate: string;
  purpose: string;
  vendor: string;
  ledgerHead: string;
  ledgerItem: string;
  voucherNumber: string;
  referenceNumber: string;
  amount: string;
  tdsNature: string;
  tdsPercent: string;
  gstPercent: string;
  note: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (): BillForm => ({
  billDate: today(),
  purpose: "",
  vendor: "",
  ledgerHead: "",
  ledgerItem: "",
  voucherNumber: "",
  referenceNumber: "",
  amount: "",
  tdsNature: "",
  tdsPercent: "0",
  gstPercent: "0",
  note: "",
});
const tdsNatureOptions = [
  { code: "1009", percent: "10", description: "Rent other than machinery" },
  { code: "1023", percent: "1", description: "Contractor - Individual/HUF" },
  { code: "1024", percent: "2", description: "Contractor other than Individual/HUF" },
  { code: "1027", percent: "10", description: "Professional services" },
];
const tdsNatureCode = (nature: string) =>
  tdsNatureOptions.find(
    ({ code }) => nature === code || nature.startsWith(`TDS - ${code} @ `),
  )?.code ?? "";
const display = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const label = record.vendorName ?? record.name ?? record.label;
    return typeof label === "string" || typeof label === "number"
      ? String(label)
      : "—";
  }
  return typeof value === "string" || typeof value === "number" ? String(value) : "—";
};
const currency = (value: unknown) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));
const formatDate = (value: unknown) => {
  if (!value) return "—";
  if (!(value instanceof Date) && typeof value !== "string" && typeof value !== "number")
    return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB");
};
const formatBillDate = (value: unknown) => formatDate(value).replace(/\//g, "-");
const tallyApiUrl = (() => {
  const url = new URL(import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5000/api/v1");
  url.pathname = url.pathname.replace(/\/api\/v1\/?$/, "/api/miscellanies");
  return url.toString();
})();

export const MiscellaneousBillsPage = ({ tab }: { tab: BillTab }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [form, setForm] = useState<BillForm>(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editingBillId, setEditingBillId] = useState<string | null>(null);
  const [modalBillNumber, setModalBillNumber] = useState("");
  const [modalBillStatus, setModalBillStatus] = useState<"pending" | "verified" | "approved">("pending");
  const [modalMode, setModalMode] = useState<"edit" | "view" | null>(null);
  const [filters, setFilters] = useState({ vendor: "", ledgerHead: "", ledgerItem: "", fromDate: "", toDate: "", search: "" });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState("");
  const [attachmentDragging, setAttachmentDragging] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraDevices, setCameraDevices] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraError, setCameraError] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const attachmentPreviewUrlRef = useRef<string | null>(null);
  const vendors = useStoreLookup("vendors", { vendorScope: "miscellaneous" });
  const ledgerHeads = useStoreLookup("masters", { kind: "expense-ledger-head" });
  const ledgerItems = useStoreLookup("masters", { kind: "expense-ledger-item" });
  const bills = useStoreRows("miscellaneous-bills", true, {
    ...(tab === "verify" && appliedFilters.vendor ? { vendor: appliedFilters.vendor } : {}),
    ...(tab === "verify" && appliedFilters.ledgerHead ? { ledgerHead: appliedFilters.ledgerHead } : {}),
    ...(tab === "verify" && appliedFilters.ledgerItem ? { ledgerItem: appliedFilters.ledgerItem } : {}),
    ...(tab === "verify" && appliedFilters.fromDate ? { fromDate: appliedFilters.fromDate } : {}),
    ...(tab === "verify" && appliedFilters.toDate ? { toDate: appliedFilters.toDate } : {}),
    ...(tab === "verify" && appliedFilters.search ? { search: appliedFilters.search } : {}),
  });
  const createBill = useMutation({
    mutationFn: (payload: object) =>
      createStoreRecord("miscellaneous-bills", payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["store"] }),
  });
  const updateBill = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: object }) =>
      patchStoreRecord("miscellaneous-bills", id, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["store"] }),
  });
  const verifyBill = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "verified" | "approved" }) =>
      updateStoreMiscellaneousBillStatus(id, status),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["store"] }),
  });
  const billAmount = Number(form.amount) || 0;
  const tdsAmount = Number((billAmount * (Number(form.tdsPercent) || 0) / 100).toFixed(2));
  const gstAmount = Number((billAmount * (Number(form.gstPercent) || 0) / 100).toFixed(2));
  const finalAmount = Math.max(0, Number((billAmount + gstAmount - tdsAmount).toFixed(2)));

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (attachmentPreviewUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(attachmentPreviewUrlRef.current);
      attachmentPreviewUrlRef.current = null;
    }
  }, []);

  const setAttachmentPreview = (url: string) => {
    if (attachmentPreviewUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(attachmentPreviewUrlRef.current);
    }
    attachmentPreviewUrlRef.current = url.startsWith("blob:") ? url : null;
    setAttachmentPreviewUrl(url);
  };

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCameraStream = async (deviceId?: string) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser does not support camera capture.");
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: "environment" } },
        audio: false,
      });

      stopCameraStream();
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch(() => undefined);
      }
      setCameraError("");
    } catch (error) {
      console.error("Camera access error:", error);
      setCameraError("Camera access was blocked. Please allow access or choose a file instead.");
      showToast("Camera access was blocked. Please allow access or use Browse.", "error");
    }
  };

  const openCamera = async () => {
    setCameraOpen(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === "videoinput");
      const mapped = videoDevices.map((device, index) => ({
        id: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      }));
      const preferred = videoDevices.find((device) => /rear|environment|back/i.test(device.label)) ?? videoDevices[0];
      setCameraDevices(mapped);
      setSelectedCameraId(preferred?.deviceId ?? "");
      await startCameraStream(preferred?.deviceId);
    } catch (error) {
      console.error("Camera enumeration error:", error);
      setCameraDevices([]);
      setSelectedCameraId("");
      await startCameraStream();
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraError("");
  };

  const captureCameraImage = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext("2d");
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    setAttachmentPreview(canvas.toDataURL("image/jpeg", 0.9));
    closeCamera();
    showToast("Photo captured from camera.", "success");
  };

  const clearAttachmentSelection = () => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraError("");
    setAttachmentPreview("");
    attachmentPreviewUrlRef.current = null;
    setAttachmentDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selectAttachment = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Choose an image file for the bill attachment.", "error");
      return;
    }
    setAttachmentPreview(URL.createObjectURL(file));
  };

  const handleAttachmentSelect = (event: ChangeEvent<HTMLInputElement>) => {
    selectAttachment(event.target.files?.[0]);
  };

  const handleAttachmentDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setAttachmentDragging(false);
    selectAttachment(event.dataTransfer.files[0]);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.billDate || !form.purpose.trim() || !form.vendor || !form.amount) {
      showToast("Complete all required bill fields.", "error");
      return;
    }
    try {
      const payload = {
        billDate: form.billDate,
        purpose: form.purpose.trim(),
        vendor: form.vendor,
        ledgerHead: form.ledgerHead || null,
        ledgerItem: form.ledgerItem || null,
        voucherNumber: form.voucherNumber.trim() || null,
        referenceNumber: form.referenceNumber.trim() || null,
        amount: Number(form.amount),
        tdsNature: form.tdsNature.trim() || null,
        tdsPercent: Number(form.tdsPercent) || 0,
        gstPercent: Number(form.gstPercent) || 0,
        note: form.note.trim() || null,
      };
      if (editingBillId) await updateBill.mutateAsync({ id: editingBillId, payload });
      else await createBill.mutateAsync(payload);
      setForm(emptyForm());
      clearAttachmentSelection();
      setShowForm(false);
      showToast(editingBillId ? "Miscellaneous bill updated successfully." : "Miscellaneous bill created and sent for verification.", "success");
      setEditingBillId(null);
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError ? reason.message : "The bill could not be created.",
        "error",
      );
    }
  };

  const editBill = (bill: Record<string, unknown>) => {
    const referenceId = (value: unknown) => {
      if (typeof value === "string") return value;
      if (value && typeof value === "object") {
        const record = value as Record<string, unknown>;
        const id = record.id ?? record._id;
        return typeof id === "string" ? id : "";
      }
      return "";
    };
    const date = new Date(typeof bill.billDate === "string" ? bill.billDate : "");
    setForm({
      billDate: Number.isNaN(date.getTime()) ? today() : date.toISOString().slice(0, 10),
      purpose: typeof bill.purpose === "string" ? bill.purpose : "",
      vendor: referenceId(bill.vendor),
      ledgerHead: referenceId(bill.ledgerHead),
      ledgerItem: referenceId(bill.ledgerItem),
      voucherNumber: typeof bill.voucherNumber === "string" ? bill.voucherNumber : "",
      referenceNumber: typeof bill.referenceNumber === "string" ? bill.referenceNumber : "",
      amount: typeof bill.amount === "number" ? String(bill.amount) : "",
      tdsNature: typeof bill.tdsNature === "string" ? bill.tdsNature : "",
      tdsPercent: typeof bill.tdsPercent === "number" ? String(bill.tdsPercent) : "0",
      gstPercent: typeof bill.gstPercent === "number" ? String(bill.gstPercent) : "0",
      note: typeof bill.note === "string" ? bill.note : "",
    });
    setEditingBillId(String(bill.id));
    setModalBillNumber(typeof bill.billNumber === "string" ? bill.billNumber : "");
    setModalBillStatus(bill.status === "verified" || bill.status === "approved" ? bill.status : "pending");
    setModalMode("edit");
  };

  const viewBill = (bill: Record<string, unknown>) => {
    editBill(bill);
    setModalMode("view");
  };

  const closeBillModal = () => {
    setModalMode(null);
    setEditingBillId(null);
    setModalBillNumber("");
    setModalBillStatus("pending");
    setForm(emptyForm());
  };

  const saveBillModal = async (nextStatus?: "verified" | "approved") => {
    if (!editingBillId) return;
    if (
      !form.billDate ||
      !form.purpose.trim() ||
      !form.vendor ||
      !form.amount ||
      !form.voucherNumber.trim() ||
      !form.referenceNumber.trim() ||
      !form.ledgerHead ||
      !form.ledgerItem ||
      !form.tdsNature
    ) {
      showToast("Complete all required bill fields.", "error");
      return;
    }
    const payload = {
      billDate: form.billDate,
      purpose: form.purpose.trim(),
      vendor: form.vendor,
      ledgerHead: form.ledgerHead || null,
      ledgerItem: form.ledgerItem || null,
      voucherNumber: form.voucherNumber.trim() || null,
      referenceNumber: form.referenceNumber.trim() || null,
      amount: Number(form.amount),
      tdsNature: form.tdsNature.trim() || null,
      tdsPercent: Number(form.tdsPercent) || 0,
      gstPercent: Number(form.gstPercent) || 0,
      note: form.note.trim() || null,
    };
    try {
      await updateBill.mutateAsync({ id: editingBillId, payload });
      if (nextStatus) {
        try {
          await verifyBill.mutateAsync({ id: editingBillId, status: nextStatus });
        } catch (reason: unknown) {
          showToast(
            reason instanceof ApiError
              ? `Bill updated, but ${nextStatus} failed: ${reason.message}`
              : `Bill updated, but ${nextStatus} failed.`,
            "error",
          );
          return;
        }
        setModalBillStatus(nextStatus);
        showToast(nextStatus === "verified" ? "Bill updated and verified." : "Bill updated and approved.", "success");
        closeBillModal();
      } else {
        showToast("Bill updated successfully.", "success");
        closeBillModal();
      }
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError ? reason.message : "The bill could not be updated.",
        "error",
      );
    }
  };

  const verify = async (id: string) => {
    try {
      const bill = bills.data?.data.find((row) => String(row.id) === id);
      const status = bill?.status === "verified" ? "approved" : "verified";
      await verifyBill.mutateAsync({ id, status });
      showToast(status === "verified" ? "Bill verified successfully." : "Bill approved successfully.", "success");
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError ? reason.message : "The bill could not be verified.",
        "error",
      );
    }
  };

  return (
    <main className="page-stack store-page miscellaneous-bills-page">
      <ModuleHero
        eyebrow="Store"
        title={showForm ? (editingBillId ? "Edit Miscellaneous Bill" : "Create Miscellaneous Bill") : "Miscellaneous Bills"}
        description="Create, review, and verify miscellaneous expense bills."
        icon={IconStore}
        tone="store"
      />
      <div className="miscellaneous-bill-tabbar">
        <div className="store-tabs" role="tablist" aria-label="Miscellaneous bill tabs">
          <Link
            className={`store-tab${tab === "create" ? " active" : ""}`}
            role="tab"
            aria-selected={tab === "create"}
            to="/store/miscellaneous-bills"
            onClick={() => { setShowForm(false); setEditingBillId(null); setModalMode(null); }}
          >
            Create
          </Link>
          <Link
            className={`store-tab${tab === "verify" ? " active" : ""}`}
            role="tab"
            aria-selected={tab === "verify"}
            to="/store/miscellaneous-bills/verify"
            onClick={() => { setShowForm(false); setEditingBillId(null); setModalMode(null); }}
          >
            Verify
          </Link>
        </div>
        <a
          className="button button-primary miscellaneous-bill-tally-api"
          href={tallyApiUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Tally Json
        </a>
      </div>

      {showForm ? (
        <section className="content-card miscellaneous-bill-form-card">
          <div className="section-heading">
            <h2>{editingBillId ? "Edit Miscellaneous Bill" : "Add Miscellaneous Bill"}</h2>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                setForm(emptyForm());
                clearAttachmentSelection();
                setShowForm(false);
                setEditingBillId(null);
              }}
            >
              Back
            </button>
          </div>
          <form
            className="compact-form miscellaneous-bill-form"
            onSubmit={(event) => void submit(event)}
          >
            <div className="form-grid">
              <label className="field field-sm">
                <span className="field-label">
                  Bill Date <span className="required-marker" aria-hidden="true">*</span>
                </span>
                <input
                  required
                  type="date"
                  value={form.billDate}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, billDate: event.target.value }))
                  }
                />
              </label>
              <label className="field field-lg">
                <span className="field-label">
                  Bill Purpose <span className="required-marker" aria-hidden="true">*</span>
                </span>
                <input
                  required
                  maxLength={500}
                  value={form.purpose}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, purpose: event.target.value }))
                  }
                />
              </label>
              <label className="field field-lg">
                <span className="field-label">
                  Service Provider <span className="required-marker" aria-hidden="true">*</span>
                </span>
                <SearchableSelect
                  value={form.vendor}
                  options={(vendors.data ?? []).map(({ id, label }) => ({
                    value: id,
                    label,
                  }))}
                  placeholder="Select a service provider"
                  onChange={(vendor) =>
                    setForm((current) => ({ ...current, vendor }))
                  }
                />
              </label>
              <label className="field field-lg">
                <span className="field-label">Ledger Head</span>
                <SearchableSelect
                  value={form.ledgerHead}
                  options={(ledgerHeads.data ?? []).map(({ id, label }) => ({
                    value: id,
                    label,
                  }))}
                  placeholder="Select a ledger head"
                  onChange={(ledgerHead) =>
                    setForm((current) => ({ ...current, ledgerHead, ledgerItem: "" }))
                  }
                  clearable
                />
              </label>
              <label className="field field-sm">
                <span className="field-label">
                  Bill Amount <span className="required-marker" aria-hidden="true">*</span>
                </span>
                <input
                  required
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.amount}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, amount: event.target.value }))
                  }
                />
              </label>
              <label className="field miscellaneous-bill-note-field">
                <span className="field-label">Note</span>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={form.note}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, note: event.target.value }))
                  }
                />
              </label>
              <div className="field miscellaneous-bill-upload-wrap">
                <span className="field-label">Invoice</span>
                <div
                  className={`miscellaneous-bill-dropzone${attachmentDragging ? " is-dragging" : ""}${attachmentPreviewUrl ? " has-preview" : ""}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setAttachmentDragging(true);
                  }}
                  onDragLeave={(event) => {
                    if (event.currentTarget === event.target) setAttachmentDragging(false);
                  }}
                  onDrop={handleAttachmentDrop}
                >
                  {attachmentPreviewUrl ? (
                    <div className="miscellaneous-bill-image-preview">
                      <img src={attachmentPreviewUrl} alt="Bill attachment preview" />
                      <div className="miscellaneous-bill-preview-actions">
                        <strong>Image ready to attach</strong>
                        <span>Preview your bill image before saving.</span>
                        <div>
                          <button
                            className="button button-secondary"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Choose another
                          </button>
                          <button
                            className="button button-secondary"
                            type="button"
                            onClick={clearAttachmentSelection}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="miscellaneous-bill-dropzone-empty">
                      <span className="miscellaneous-bill-dropzone-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
                          <path d="M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14" />
                        </svg>
                      </span>
                      <strong>Attach a bill image</strong>
                      <span>Drag and drop an image here, or choose one from your device.</span>
                      <div className="miscellaneous-bill-dropzone-actions">
                        <button
                          className="button button-primary"
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Browse images
                        </button>
                        <button
                          className="miscellaneous-bill-camera-button"
                          type="button"
                          onClick={() => void openCamera()}
                        >
                          <span aria-hidden="true">◉</span>
                          Take photo
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleAttachmentSelect}
                  />
                </div>
                {cameraOpen ? (
                  <div className="miscellaneous-bill-camera-modal" role="dialog" aria-modal="true">
                    <div className="miscellaneous-bill-camera-panel">
                      <div className="miscellaneous-bill-camera-header">
                        <strong>Take a photo</strong>
                        <button type="button" className="button button-secondary" onClick={closeCamera}>
                          Close
                        </button>
                      </div>
                      {cameraDevices.length > 1 ? (
                        <label className="field field-full miscellaneous-bill-camera-select">
                          <span className="field-label">Camera</span>
                          <select
                            value={selectedCameraId}
                            onChange={(event) => {
                              const nextDeviceId = event.target.value;
                              setSelectedCameraId(nextDeviceId);
                              if (nextDeviceId) void startCameraStream(nextDeviceId);
                            }}
                          >
                            {cameraDevices.map((device) => (
                              <option key={device.id} value={device.id}>
                                {device.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                      {cameraError ? <p className="miscellaneous-bill-camera-error">{cameraError}</p> : null}
                      <video ref={videoRef} autoPlay playsInline muted className="miscellaneous-bill-camera-video" />
                      <div className="miscellaneous-bill-camera-actions">
                        <button type="button" className="button button-secondary" onClick={closeCamera}>
                          Cancel
                        </button>
                        <button type="button" className="button button-primary" onClick={captureCameraImage}>
                          Capture
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="form-actions">
              <button
                className="button button-primary"
                type="submit"
                disabled={createBill.isPending}
              >
                {createBill.isPending ? "Saving…" : "Save Bill"}
              </button>
              <button
                className="button button-secondary"
                type="button"
                onClick={() => {
                  setForm(emptyForm());
                  clearAttachmentSelection();
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {!showForm || tab === "verify" ? (
        <section className="content-card">
          <div className="section-heading">
            <h2>{tab === "verify" ? "Miscellaneous Bills" : "Miscellaneous Bills List"}</h2>
            {tab === "create" ? (
              <button
                className="button button-primary"
                type="button"
                onClick={() => {
                  setForm(emptyForm());
                  setShowForm(true);
                }}
              >
                + Create Bill
              </button>
            ) : null}
          </div>
        {tab === "verify" ? (
        <div className="miscellaneous-bill-filters">
          <label className="field">
            <span className="field-label">Vendor</span>
            <SearchableSelect
              value={filters.vendor}
              options={(vendors.data ?? []).map(({ id, label }) => ({ value: id, label }))}
              placeholder="All Vendors"
              onChange={(vendor) => setFilters((current) => ({ ...current, vendor }))}
              clearable
            />
          </label>
          <label className="field">
            <span className="field-label">Ledger Head</span>
            <SearchableSelect
              value={filters.ledgerHead}
              options={(ledgerHeads.data ?? []).map(({ id, label }) => ({ value: id, label }))}
              placeholder="All Ledger Heads"
              onChange={(ledgerHead) => setFilters((current) => ({ ...current, ledgerHead, ledgerItem: "" }))}
              clearable
            />
          </label>
          <label className="field">
            <span className="field-label">Ledger Item</span>
            <SearchableSelect
              value={filters.ledgerItem}
              options={(ledgerItems.data ?? [])
                .filter((item) => !filters.ledgerHead || item.parent === filters.ledgerHead)
                .map(({ id, label }) => ({ value: id, label }))}
              placeholder="All Ledger Items"
              onChange={(ledgerItem) => setFilters((current) => ({ ...current, ledgerItem }))}
              clearable
            />
          </label>
          <label className="field">
            <span className="field-label">From Date</span>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">To Date</span>
            <input
              type="date"
              value={filters.toDate}
              onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))}
            />
          </label>
          <label className="field miscellaneous-bill-search">
            <span className="field-label">Search</span>
            <input
              value={filters.search}
              placeholder="Bill number or purpose"
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            />
          </label>
          <div className="miscellaneous-bill-filter-actions">
            <button className="button button-primary" type="button" onClick={() => setAppliedFilters(filters)}>
              Filter
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                const reset = { vendor: "", ledgerHead: "", ledgerItem: "", fromDate: "", toDate: "", search: "" };
                setFilters(reset);
                setAppliedFilters(reset);
              }}
            >
              Reset
            </button>
          </div>
        </div>
        ) : null}
        {bills.isLoading ? <LoadingState columns={tab === "verify" ? 10 : 8} /> : null}
        {bills.isError ? (
          <ErrorState
            title="Unable to load bills"
            message="Please try again."
            action={{ label: "Try again", onClick: () => void bills.refetch() }}
          />
        ) : null}
        {bills.data && bills.data.data.length === 0 && !bills.isLoading ? (
          <EmptyState
            title="No miscellaneous bills found"
            message={
              tab === "verify"
                ? "New miscellaneous bills will appear here."
                : "Create a miscellaneous bill to start tracking expenses."
            }
          />
        ) : null}
        {bills.data && bills.data.data.length > 0 ? (
          <div className="table-scroll">
            <table className={tab === "verify" ? "miscellaneous-bill-verify-table" : "miscellaneous-bill-list-table"}>
              <thead>
                {tab === "verify" ? (
                  <tr>
                    <th>#</th>
                    <th>Bill Date</th>
                    <th>Bill No</th>
                    <th>Service Provider</th>
                    <th>Ledger Head</th>
                    <th>Ledger Item</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Created / Verified By</th>
                    <th>Action</th>
                  </tr>
                ) : (
                  <tr>
                    <th>#</th>
                    <th>Bill Date</th>
                    <th>Bill No</th>
                    <th>Bill Purpose</th>
                    <th>Service Provider</th>
                    <th>Ledger Head</th>
                    <th>Bill Amount</th>
                    <th>Status</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {bills.data.data.map((bill, index) => (
                  <tr key={String(bill.id)}>
                    {tab === "verify" ? (
                      <>
                        <td>{index + 1}</td>
                        <td>{formatBillDate(bill.billDate)}</td>
                        <td>{display(bill.billNumber)}</td>
                        <td>{display(bill.vendor)}</td>
                        <td>{display(bill.ledgerHead)}</td>
                        <td>{display(bill.ledgerItem)}</td>
                        <td>{currency(bill.finalAmount ?? bill.amount)}</td>
                        <td>
                          <span className={`miscellaneous-bill-status miscellaneous-bill-status-${String(bill.status)}`}>
                            {display(bill.status)}
                          </span>
                        </td>
                        <td className="miscellaneous-bill-audit">
                          <span>Created by: <strong>{display(bill.createdBy)}</strong></span>
                          <span>Verified by: <strong>{display(bill.verifiedBy)}</strong></span>
                          <span>Approved by: <strong>{display(bill.approvedBy)}</strong></span>
                        </td>
                        <td>
                          <div className="miscellaneous-bill-row-actions">
                            <button
                              className="miscellaneous-bill-icon-action"
                              type="button"
                              aria-label={`View bill ${display(bill.billNumber)}`}
                              title="View bill"
                              onClick={() => viewBill(bill)}
                            >
                              <IconEye />
                            </button>
                            {bill.status === "pending" || bill.status === "verified" ? (
                              <button
                                className="miscellaneous-bill-icon-action edit"
                                type="button"
                                aria-label={`Edit bill ${display(bill.billNumber)}`}
                                title="Edit and verify bill"
                                onClick={() => editBill(bill)}
                              >
                                <IconEdit />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{index + 1}</td>
                        <td>{formatDate(bill.billDate)}</td>
                        <td>{display(bill.billNumber)}</td>
                        <td>{display(bill.purpose)}</td>
                        <td>{display(bill.vendor)}</td>
                        <td>{display(bill.ledgerHead)}</td>
                        <td>{currency(bill.amount)}</td>
                        <td>
                          <span className={`status-badge ${bill.status === "approved" ? "active" : "warning"}`}>
                            {display(bill.status)}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        </section>
      ) : null}
      {modalMode ? (
        <div className="miscellaneous-bill-edit-overlay" role="presentation">
          <section
            className="miscellaneous-bill-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="miscellaneous-bill-edit-title"
          >
            <header className="miscellaneous-bill-edit-header">
              <h2 id="miscellaneous-bill-edit-title">
                {modalMode === "view" ? "View Account Miscellaneous Bill" : "Edit Account Miscellaneous Bill"}
              </h2>
              <button type="button" aria-label="Close" onClick={closeBillModal}>×</button>
            </header>
            <form
              className="miscellaneous-bill-edit-form"
              onSubmit={(event) => {
                event.preventDefault();
                void saveBillModal();
              }}
            >
              <div className="miscellaneous-bill-edit-grid">
                <label className="field">
                  <span className="field-label">Bill No <span className="required-marker">*</span></span>
                  <input value={modalBillNumber} readOnly />
                </label>
                <label className="field miscellaneous-bill-edit-span-2">
                  <span className="field-label">Bill Purpose <span className="required-marker">*</span></span>
                  <input
                    required
                    maxLength={500}
                    value={form.purpose}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, purpose: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Bill Date <span className="required-marker">*</span></span>
                  <input
                    required
                    type="date"
                    value={form.billDate}
                    disabled={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, billDate: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Service Provider <span className="required-marker">*</span></span>
                  <SearchableSelect
                    value={form.vendor}
                    options={(vendors.data ?? []).map(({ id, label }) => ({ value: id, label }))}
                    placeholder="Select a service provider"
                    disabled={modalMode === "view"}
                    onChange={(vendor) => setForm((current) => ({ ...current, vendor }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Bill Amount <span className="required-marker">*</span></span>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.amount}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Final Bill Amount</span>
                  <input value={finalAmount.toFixed(2)} readOnly />
                </label>
                <label className="field">
                  <span className="field-label">Voucher Number <span className="required-marker">*</span></span>
                  <input
                    required
                    maxLength={60}
                    value={form.voucherNumber}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, voucherNumber: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Reference Number <span className="required-marker">*</span></span>
                  <input
                    required
                    maxLength={100}
                    value={form.referenceNumber}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, referenceNumber: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Ledger Head <span className="required-marker">*</span></span>
                  <SearchableSelect
                    value={form.ledgerHead}
                    options={(ledgerHeads.data ?? []).map(({ id, label }) => ({ value: id, label }))}
                    placeholder="Select ledger head"
                    disabled={modalMode === "view"}
                    clearable
                    onChange={(ledgerHead) => setForm((current) => ({ ...current, ledgerHead, ledgerItem: "" }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Ledger Item <span className="required-marker">*</span></span>
                  <SearchableSelect
                    value={form.ledgerItem}
                    options={(ledgerItems.data ?? [])
                      .filter((item) => !form.ledgerHead || item.parent === form.ledgerHead)
                      .map(({ id, label }) => ({ value: id, label }))}
                    placeholder="Select ledger item"
                    disabled={modalMode === "view"}
                    clearable
                    onChange={(ledgerItem) => setForm((current) => ({ ...current, ledgerItem }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">TDS Nature of Payment <span className="required-marker">*</span></span>
                  <select
                    value={tdsNatureCode(form.tdsNature)}
                    disabled={modalMode === "view"}
                    onChange={(event) => {
                      const selected = tdsNatureOptions.find((option) => option.code === event.target.value);
                      setForm((current) => ({
                        ...current,
                        tdsNature: selected ? `TDS - ${selected.code} @ ${selected.percent}% (${selected.description})` : "",
                        tdsPercent: selected?.percent ?? "0",
                      }));
                    }}
                  >
                    <option value="">Select TDS nature…</option>
                    {tdsNatureOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        TDS - {option.code} @ {option.percent}% ({option.description})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">TDS Percentage</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.tdsPercent}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, tdsPercent: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">TDS Deduction Amount</span>
                  <input value={tdsAmount.toFixed(2)} readOnly />
                </label>
                <label className="field">
                  <span className="field-label">GST (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.gstPercent}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, gstPercent: event.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">GST Amount</span>
                  <input value={gstAmount.toFixed(2)} readOnly />
                </label>
                <label className="field">
                  <span className="field-label">Total Amount</span>
                  <input className="miscellaneous-bill-total-input" value={finalAmount.toFixed(2)} readOnly />
                </label>
                <label className="field miscellaneous-bill-edit-span-3">
                  <span className="field-label">Note</span>
                  <textarea
                    rows={2}
                    maxLength={2000}
                    value={form.note}
                    readOnly={modalMode === "view"}
                    onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
                  />
                </label>
              </div>
              <footer className="miscellaneous-bill-edit-actions">
                <button className="button button-secondary" type="button" onClick={closeBillModal}>
                  {modalMode === "view" ? "Close" : "Cancel"}
                </button>
                {modalMode === "edit" ? (
                  <>
                    <button className="button button-primary" type="submit" disabled={updateBill.isPending || verifyBill.isPending}>
                      {updateBill.isPending ? "Updating…" : "Update Bill"}
                    </button>
                    {modalBillStatus === "pending" ? (
                      <button
                        className="button miscellaneous-bill-verify-modal-button"
                        type="button"
                        disabled={updateBill.isPending || verifyBill.isPending}
                        onClick={() => void saveBillModal("verified")}
                      >
                        {verifyBill.isPending ? "Verifying…" : "Update & Verify"}
                      </button>
                    ) : null}
                    {modalBillStatus === "verified" ? (
                      <button
                        className="button miscellaneous-bill-approve-button"
                        type="button"
                        disabled={updateBill.isPending || verifyBill.isPending}
                        onClick={() => void saveBillModal("approved")}
                      >
                        {verifyBill.isPending ? "Approving…" : "Update & Approve"}
                      </button>
                    ) : null}
                  </>
                ) : null}
              </footer>
            </form>
          </section>
        </div>
      ) : null}
    </main>
  );
};
